import { InputBaseComponentProps } from "@mui/material";
import React from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";

export type CurrencyCode = "EUR" | "GBP" | "USD";

export const isValidCurrency = (currency?: string): currency is CurrencyCode =>
  currency !== undefined && ["EUR", "USD", "GBP"].includes(currency);

export const getCurrencyCode: (currencyCode?: string) => CurrencyCode = (
  code,
) => (isValidCurrency(code) ? code : "GBP");

export interface Currency {
  code: CurrencyCode;
  symbol: string;
  format: (value: number) => string;
  // abbreviated form for tight spaces, e.g. £1M, £400K, −£200K
  formatCompact: (value: number) => string;
  // the input component of an MUI text field
  createNumericFormat: (
    min?: number,
    max?: number,
  ) => React.ElementType<InputBaseComponentProps>;
}

const gbpFormat = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

const gbpCompactFormat = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  notation: "compact",
  maximumFractionDigits: 1,
});

export const gbp: Currency = {
  code: "GBP",
  symbol: "£",
  format: (value) => gbpFormat.format(value),
  formatCompact: (value) => gbpCompactFormat.format(value),
  createNumericFormat(min, max) {
    return createFormat(this.symbol, min, max);
  },
};

const eurFormat = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
});

const eurCompactFormat = new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  notation: "compact",
  maximumFractionDigits: 1,
});

const eur: Currency = {
  code: "EUR",
  symbol: "€",
  format: (value) => eurFormat.format(value),
  formatCompact: (value) => eurCompactFormat.format(value),
  createNumericFormat(min, max) {
    return createFormat(this.symbol, min, max);
  },
};

const usdFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const usdCompactFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

const usd: Currency = {
  code: "USD",
  symbol: "$",
  format: (value) => usdFormat.format(value),
  formatCompact: (value) => usdCompactFormat.format(value),
  createNumericFormat(min, max) {
    return createFormat(this.symbol, min, max);
  },
};

// live formatting for text input fields
interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

function createFormat(
  symbol: string,
  min?: number,
  max?: number,
): React.ElementType<InputBaseComponentProps> {
  // MUI types the change handler of its input component for DOM events; this
  // one calls it with the bare name and value, so the two types do not meet
  return React.forwardRef<NumericFormatProps, CustomProps>(
    function NumericFormatCustom(props, ref) {
      const { onChange, ...other } = props;

      return (
        <NumericFormat
          {...other}
          getInputRef={ref}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value,
              },
            });
          }}
          thousandSeparator
          valueIsNumericString
          prefix={symbol}
          allowLeadingZeros={false}
          decimalScale={2}
          min={min}
          max={max}
          isAllowed={(values) => {
            const { floatValue } = values;
            if (floatValue === undefined) return true;
            if (min !== undefined && floatValue < min) return false;
            if (max !== undefined && floatValue > max) return false;
            return true;
          }}
          fixedDecimalScale
        />
      );
    },
  ) as unknown as React.ElementType<InputBaseComponentProps>;
}

export function getCurrency(code?: string): Currency {
  switch (code) {
    case "EUR":
      return eur;
    case "USD":
      return usd;
    default:
      return gbp;
  }
}
